import { Component, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { CoreConfigService } from '@core/services/config.service';

import { AppPermissionsService, AuthenticationService } from '@common/services';

import { ChatContentComponent } from '../chat-content/chat-content.component';
import { ChatDockService } from '../services/chat-dock.service';
import { ChatSessionService } from '../services/chat-session.service';

/** Presets de ancho ofrecidos en la cabecera del panel */
const WIDTH_PRESETS = [30, 40, 50];

/**
 * Contenedor del chat de Bryant en modo "dock": una burbuja fija en la esquina
 * inferior derecha que se expande a un panel lateral. El panel no tapa la app,
 * la empuja (ver `bl-dock-open` en el SCSS de este componente).
 */
@Component({
  selector: 'app-chat-dock',
  templateUrl: './chat-dock.component.html',
  styleUrls: ['./chat-dock.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ChatDockComponent implements OnInit, OnDestroy {

  public canSeeAssistant: boolean = false;
  public isOpen: boolean = false;
  public width: number;
  public isResizing: boolean = false;
  /** `true` cuando el usuario ya está en la página completa del chat */
  public isOnChatPage: boolean = false;
  /** Bryant está respondiendo, aunque el panel esté cerrado */
  public isBusy: boolean = false;
  /** Llegó una respuesta mientras el panel estaba cerrado */
  public hasUnread: boolean = false;

  public readonly presets = WIDTH_PRESETS;

  public bixAvatar: string = 'https://belivthumbs.blob.core.windows.net/prod/bot.png';

  @ViewChild(ChatContentComponent) chatContent: ChatContentComponent;

  private _unsubscribeAll: Subject<any>;
  /** Referencias a los listeners del arrastre, para poder desmontarlos */
  private _onDragMove: (event: MouseEvent | TouchEvent) => void;
  private _onDragEnd: () => void;

  /**
   * Constructor
   * @param _dockService
   * @param _permissionsService
   * @param _router
   * @param _translateService
   * @param _session Conversación activa, se lee para avisar desde la burbuja
   */
  constructor(
    private _dockService: ChatDockService,
    private _session: ChatSessionService,
    private _permissionsService: AppPermissionsService,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _router: Router,
    private _translateService: TranslateService
  ) {
    this._unsubscribeAll = new Subject();
    this.width = this._dockService.width;
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    // El chat solo existe si se cumplen las tres condiciones a la vez:
    //   1. el usuario tiene permiso de lectura sobre el chat,
    //   2. hay sesión iniciada,
    //   3. la pantalla admite chrome de la app.
    //
    // La tercera es la que deja fuera al login: esas páginas (login, error,
    // sesión expirada, no autorizado, mantenimiento) se configuran con el
    // navbar oculto, y ahí ni la burbuja ni el panel deben aparecer.
    combineLatest([
      this._permissionsService.onPermissionsChanged,
      this._authenticationService.onUserValueChanged,
      // El getter está tipado como `any | Observable<any>`, hay que concretarlo
      this._coreConfigService.config as Observable<any>
    ])
      .pipe(
        map(([permissions, user, config]) =>
          !!permissions
          && !!user
          && !config?.layout?.navbar?.hidden
          && this._permissionsService.hasPermissionByUrl('pages/chat', 'READ')
        ),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(canSee => {
        this.canSeeAssistant = canSee;
        // El servicio recoge la clase de <body> y recuerda si estaba abierto,
        // así vuelve como estaba al recuperar la sesión.
        this._dockService.setEnabled(canSee);
      });

    this._dockService.open$.pipe(takeUntil(this._unsubscribeAll)).subscribe(open => {
      this.isOpen = open;
      if (open) {
        // Espera a que el panel exista en el DOM para bajar el scroll
        setTimeout(() => this.chatContent?.scrollToBottom(), 60);
      }
    });

    this._dockService.width$.pipe(takeUntil(this._unsubscribeAll)).subscribe(w => (this.width = w));

    // La respuesta sigue llegando con el panel cerrado: la burbuja lo muestra
    this._session.busy$.pipe(takeUntil(this._unsubscribeAll)).subscribe(busy => (this.isBusy = busy));
    this._session.unread$.pipe(takeUntil(this._unsubscribeAll)).subscribe(unread => (this.hasUnread = unread));
    this._dockService.resizing$.pipe(takeUntil(this._unsubscribeAll)).subscribe(r => (this.isResizing = r));

    // En la página completa del chat el dock se oculta para no duplicar la conversación
    this.isOnChatPage = this.matchesChatPage(this._router.url);
    this._router.events
      .pipe(filter(event => event instanceof NavigationEnd), takeUntil(this._unsubscribeAll))
      .subscribe((event: NavigationEnd) => {
        this.isOnChatPage = this.matchesChatPage(event.urlAfterRedirects);
        if (this.isOnChatPage) this._dockService.close();
      });
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    this.detachDragListeners();
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /** Abre el panel acoplado */
  open(): void {
    this._dockService.open();
  }

  /** Cierra el panel acoplado */
  close(): void {
    this._dockService.close();
  }

  /** Alterna el panel */
  toggle(): void {
    this._dockService.toggle();
  }

  /**
   * Lleva la conversación a la página completa (una sola pantalla).
   */
  expandToPage(): void {
    this._dockService.close();
    this._router.navigate(['/pages/chat']);
  }

  /**
   * Fija el ancho del panel a uno de los presets.
   * @param {number} preset Ancho en % del viewport
   */
  applyPreset(preset: number): void {
    this._dockService.setWidth(preset);
  }

  /**
   * Inicia el arrastre del borde izquierdo para redimensionar el panel.
   * @param {MouseEvent | TouchEvent} event Evento que originó el arrastre
   */
  startResize(event: MouseEvent | TouchEvent): void {
    event.preventDefault();
    this._dockService.setResizing(true);

    this._onDragMove = (moveEvent: MouseEvent | TouchEvent) => {
      const clientX = moveEvent instanceof MouseEvent
        ? moveEvent.clientX
        : moveEvent.touches[0]?.clientX;
      if (clientX == null) return;
      // El panel crece hacia la izquierda: su ancho es la distancia al borde derecho
      this._dockService.setWidth(((window.innerWidth - clientX) / window.innerWidth) * 100, false);
    };

    this._onDragEnd = () => {
      this._dockService.setResizing(false);
      // Persiste el ancho final
      this._dockService.setWidth(this._dockService.width);
      this.detachDragListeners();
    };

    document.addEventListener('mousemove', this._onDragMove);
    document.addEventListener('touchmove', this._onDragMove, { passive: false });
    document.addEventListener('mouseup', this._onDragEnd);
    document.addEventListener('touchend', this._onDragEnd);
  }

  /**
   * Devuelve el panel a su ancho por defecto (doble clic en el borde).
   */
  resetWidth(): void {
    this._dockService.setWidth(this._dockService.limits.default);
  }

  /**
   * Cierra el panel con la tecla Escape.
   */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.close();
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas del backend de traducciones.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   * @returns {string} Texto según el idioma activo
   */
  label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }

  /** Quita los listeners globales del arrastre */
  private detachDragListeners(): void {
    if (this._onDragMove) {
      document.removeEventListener('mousemove', this._onDragMove);
      document.removeEventListener('touchmove', this._onDragMove);
    }
    if (this._onDragEnd) {
      document.removeEventListener('mouseup', this._onDragEnd);
      document.removeEventListener('touchend', this._onDragEnd);
    }
    this._onDragMove = null;
    this._onDragEnd = null;
  }

  /**
   * Indica si una url corresponde a la página completa del chat.
   * @param {string} url Url a evaluar
   */
  private matchesChatPage(url: string): boolean {
    return (url || '').split('?')[0].endsWith('/pages/chat');
  }
}
